import { useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import {
  ENABLE_MFA_MUTATION,
  DISABLE_MFA_MUTATION,
  REGENERATE_MFA_BACKUP_CODES_MUTATION,
  CONSUME_MFA_BACKUP_CODE_MUTATION,
} from "@/graphql/mutations";
import { GET_CURRENT_USER_QUERY } from "@/graphql/queries";
import QRCode from "react-qr-code";

export default function MfaSection() {
  const { data, refetch } = useQuery(GET_CURRENT_USER_QUERY);
  const mfaEnabled = data?.getCurrentUser?.mfaEnabled;
  const mfaBackupCodeCount = data?.getCurrentUser?.mfaBackupCodeCount;

  const [mfaSetup, setMfaSetup] = useState<{ uri: string; secret: string } | null>(null);
  const [backupCodes, setBackupCodes] = useState<string[] | null>(null);

  const [enableMfa, { loading: enablingMfa }] = useMutation(ENABLE_MFA_MUTATION, {
    onCompleted: (data) => {
      refetch();
      const mfa = data?.enableMfa;
      // New API: mfa.user and mfa.mfaBackupCodes
      if (mfa?.user?.mfaTotpUri && mfa?.user?.mfaTotpSecret) {
        setMfaSetup({ uri: mfa.user.mfaTotpUri, secret: mfa.user.mfaTotpSecret });
      }
      if (Array.isArray(mfa?.mfaBackupCodes)) {
        setBackupCodes(mfa.mfaBackupCodes);
        setShowBackupCodes(true);
      }
    },
  });
  const [disableMfa, { loading: disablingMfa }] = useMutation(DISABLE_MFA_MUTATION, {
    onCompleted: () => refetch(),
  });
  const [regenerateBackupCodes, { loading: regeneratingCodes }] = useMutation(REGENERATE_MFA_BACKUP_CODES_MUTATION, {
    onCompleted: (data) => {
      const payload = data?.regenerateMfaBackupCodes;
      if (Array.isArray(payload?.mfaBackupCodes)) {
        setBackupCodes(payload.mfaBackupCodes);
        setShowBackupCodes(true);
      }
      refetch();
    },
  });
  const [consumeBackupCode, { loading: consumingCode }] = useMutation(CONSUME_MFA_BACKUP_CODE_MUTATION, {
    onCompleted: () => refetch(),
  });

  const [showBackupCodes, setShowBackupCodes] = useState(false);
  const [backupCodeInput, setBackupCodeInput] = useState("");
  const [uiError, setUiError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleEnableMfa = async () => {
    setUiError(null);
    try {
      await enableMfa();
      // backupCodes and showBackupCodes are set in onCompleted
// Regenerating codes will now show the new codes to the user
    } catch (e: any) {
      setUiError(e.message);
    }
  };

  const handleCloseMfaSetup = () => {
    setMfaSetup(null);
    if (backupCodes && backupCodes.length > 0) {
      setShowBackupCodes(true);
    } else {
      setShowBackupCodes(false);
      setBackupCodes(null);
    }
  };


  const handleDisableMfa = async () => {
    if (!window.confirm("Are you sure you want to disable multi-factor authentication?")) return;
    setUiError(null);
    try {
      await disableMfa();
      setShowBackupCodes(false);
    } catch (e: any) {
      setUiError(e.message);
    }
  };

  const handleRegenerateCodes = async () => {
    if (!window.confirm("This will invalidate your old backup codes. Continue?")) return;
    setUiError(null);
    try {
      await regenerateBackupCodes();
      // setShowBackupCodes(true); // now handled in onCompleted
    } catch (e: any) {
      setUiError(e.message);
    }
  };

  const handleConsumeBackupCode = async () => {
    setUiError(null);
    try {
      await consumeBackupCode({ variables: { code: backupCodeInput } });
      setBackupCodeInput("");
      setSuccess(true);
    } catch (e: any) {
      setUiError(e.message);
      setSuccess(false);
    }
  };

  return (
    <section className="mt-10 bg-white dark:bg-gray-800 rounded-lg shadow p-6 w-full">
      <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-3">
        Multi-Factor Authentication (MFA)
      </h3>
      <p className="text-gray-700 dark:text-gray-300 mb-2">
        MFA adds an extra layer of security to your account. Use your authenticator app or backup codes to sign in.
      </p>
      <p className="text-gray-700 dark:text-gray-300 mb-2">
        Status: <span className={mfaEnabled ? "text-green-700" : "text-red-700"}>{mfaEnabled ? "Enabled" : "Disabled"}</span>
      </p>
      {typeof mfaBackupCodeCount === "number" && mfaEnabled && (
        <p className="text-gray-700 dark:text-gray-300 mb-2">
          Backup codes remaining: <span className="font-semibold">{mfaBackupCodeCount}</span>
        </p>
      )}
      {!mfaEnabled ? (
        <button
          className="inline-flex items-center rounded-md bg-teal-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-teal-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-600"
          onClick={handleEnableMfa}
          disabled={enablingMfa}
        >
          Enable MFA
        </button>
      ) : (
        <>
          <button
            className="inline-flex items-center rounded-md bg-pink-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-pink-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-pink-600 mr-2"
            onClick={handleDisableMfa}
            disabled={disablingMfa}
          >
            Disable MFA
          </button>
          <button
            className="inline-flex items-center rounded-md bg-teal-500 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-teal-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-500 mr-2"
            onClick={handleRegenerateCodes}
            disabled={regeneratingCodes}
          >
            Regenerate Backup Codes
          </button>
          <span className="block text-xs text-gray-500 mt-1">Regenerating will invalidate all old backup codes.</span>
        </>
      )}
      {/* MFA Setup Modal: Show QR code and secret after enabling MFA */}
      {mfaSetup && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white dark:bg-gray-800 p-6 rounded-lg w-full max-w-md mx-auto">
            <h3 className="text-lg font-bold mb-4 text-center">Set Up Your Authenticator App</h3>
            <p className="text-gray-700 dark:text-gray-300 mb-2 text-center">
              Scan this QR code with your authenticator app (Google Authenticator, Authy, 1Password, etc.), or enter the secret below manually.
            </p>
            <div className="flex flex-col items-center justify-center mb-4">
              <QRCode value={mfaSetup.uri} size={180} />
            </div>
            <div className="bg-gray-100 dark:bg-gray-900 rounded p-2 mb-2 text-center text-gray-900 dark:text-gray-100 select-all">
              <span className="font-mono text-lg">{mfaSetup.secret}</span>
            </div>
            <p className="text-gray-700 dark:text-gray-300 mb-4 text-center">
              After scanning, your app will generate 6-digit codes for login.
            </p>
            <button
              className="inline-flex items-center rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 w-full"
              onClick={handleCloseMfaSetup}
            >
              Done
            </button>
          </div>
        </div>
      )}
      {/* Show backup codes modal after enabling MFA */}
      {showBackupCodes && backupCodes && !mfaSetup && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white dark:bg-gray-800 p-6 rounded-lg w-full max-w-md mx-auto">
            <h3 className="text-lg font-bold mb-4 text-center">Your Backup Codes</h3>
            <p className="text-gray-700 dark:text-gray-300 mb-2 text-center">
              Save these codes in a secure place. Each code can be used once if you lose access to your authenticator app. <span className="font-semibold text-red-600">You will not be able to see these codes again!</span>
            </p>
            <ul className="bg-gray-100 dark:bg-gray-900 rounded p-3 mb-2 grid grid-cols-2 gap-2 text-center">
              {backupCodes.map((code) => (
                <li key={code} className="font-mono text-base text-gray-900 dark:text-gray-100 select-all py-1">
                  {code}
                </li>
              ))}
            </ul>
            <div className="flex justify-center gap-2 mb-2">
              <button
                className="inline-flex items-center rounded-md bg-indigo-600 px-3 py-1 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500"
                onClick={() => {
                  navigator.clipboard.writeText(backupCodes.join("\n"));
                }}
              >Copy All</button>
              <button
                className="inline-flex items-center rounded-md bg-indigo-600 px-3 py-1 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500"
                onClick={() => {
                  const blob = new Blob([backupCodes.join("\n")], { type: "text/plain" });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement("a");
                  a.href = url;
                  a.download = "mfa-backup-codes.txt";
                  a.click();
                  URL.revokeObjectURL(url);
                }}
              >Download</button>
            </div>
            <button
              className="inline-flex items-center rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 w-full"
              onClick={() => {
                setShowBackupCodes(false);
                setBackupCodes(null);
              }}
            >
              Done
            </button>
          </div>
        </div>
      )}

      {success && (
        <p className="text-green-600 mt-2 text-center">Action successful!</p>
      )}
      {showBackupCodes && backupCodes && (
        <p className="text-yellow-600 mt-2 text-center">Make sure to save your new backup codes. Old codes are now invalid.</p>
      )}
      {uiError && (
        <p className="text-red-600 mt-2 text-center">{uiError}</p>
      )}
    </section>
  );
}
